// src/App.js

import React, { useState, createContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import { awsConfig } from './aws-config';
import './App.css';
import Login from './components/Login';
import AuthHandler from './components/AuthHandler';
import Menu from './components/Menu';
import Precios from './components/Precios';
import Usuarios from './components/Usuarios';
import CrearUsuario from './components/CrearUsuario';
import Transacciones from './components/Transacciones';
import Campaigns from './components/Campaigns';
import CreateCampaign from './components/CreateCampaign';
import Entidades from './components/Entidades';
import TransaccionesPendientes from './components/TransaccionesPendientes';
import DashboardEjecutivo from './components/DashboardEjecutivo';
import ContractEditor from './components/ContractEditor';
import ContractsViewer from './components/ContractsViewer';
import GestionPreguntas from './components/GestionPreguntas';
import GestionFlujos from './components/GestionFlujos';
import BonusCodes from './components/BonusCodes';
import SetBranding from './components/SetBranding';

// Crear contexto para los datos del usuario
export const UserContext = createContext(null);

// Configurar Amplify
Amplify.configure(awsConfig);

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState({
    tenant_id: '',
    user_brand: '',
    user_email: '',
    user_id: '',
    user_name: '',
    company_name: '',
    store_name: '',
    store_city: '',
    user_type: ''
  });

  // Verificar si hay datos de usuario guardados al cargar la aplicación
  // NOTA: El AuthHandler ya maneja la verificación de autenticación con Cognito
  // Este useEffect es solo para casos donde tengamos datos en localStorage
  // pero el AuthHandler determinará la autenticación real
  useEffect(() => {
    const tenant_id = localStorage.getItem('tenant_id');
    const user_id = localStorage.getItem('user_id');
    
    //console.log('🔍 App.js: Verificando localStorage al iniciar...');
    //console.log('📝 tenant_id en localStorage:', tenant_id);
    //console.log('📝 user_id en localStorage:', user_id);
    
    if (tenant_id && user_id) {
      //console.log('✅ Datos encontrados en localStorage, recuperando...');
      // Recuperar todos los datos guardados
      setUserData({
        tenant_id: localStorage.getItem('tenant_id') || '',
        user_brand: localStorage.getItem('user_brand') || '',
        user_email: localStorage.getItem('user_email') || '',
        user_id: localStorage.getItem('user_id') || '',
        user_name: localStorage.getItem('user_name') || '',
        company_name: localStorage.getItem('company_name') || '',
        store_name: localStorage.getItem('store_name') || '',
        store_city: localStorage.getItem('store_city') || '',
        user_type: localStorage.getItem('user_type') || ''
      });
      setIsLoggedIn(true);
    } else {
      //console.log('❌ No hay datos en localStorage');
    }
  }, []);

  // Función para manejar el inicio de sesión exitoso
  const handleLoginSuccess = (responseData) => {
    setUserData({
      tenant_id: responseData.tenant_id || '',
      user_brand: responseData.user_brand || '',
      user_email: responseData.user_email || '',
      user_id: responseData.user_id || '',
      user_name: responseData.user_name || '',
      company_name: responseData.company_name || '',
      store_name: responseData.store_name || '',
      store_city: responseData.store_city || '',
      user_type: responseData.user_type || ''
    });
    setIsLoggedIn(true);
  };

  // Función para cerrar sesión
  const handleLogout = async () => {
    try {
      //console.log('🚪 Cerrando sesión...');
      
      // 1. Cerrar sesión en Cognito
      await signOut();
      //console.log('✅ Sesión de Cognito cerrada correctamente');
      
      // 2. Limpiar localStorage
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('user_brand');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('company_name');
    localStorage.removeItem('store_name');
    localStorage.removeItem('store_city');
    localStorage.removeItem('user_type');
      //console.log('✅ localStorage limpiado');
    
      // 3. Resetear estado
      setUserData({
        tenant_id: '',
        user_brand: '',
        user_email: '',
        user_id: '',
        user_name: '',
        company_name: '',
        store_name: '',
        store_city: '',
        user_type: ''
      });
      setIsLoggedIn(false);
      //console.log('✅ Estado de la aplicación resetado');
      
    } catch (error) {
      console.error('❌ Error al cerrar sesión:', error);
      
      // Aunque falle el signOut de Cognito, limpiamos el estado local
      localStorage.clear(); // Limpia todo el localStorage por si acaso
    setUserData({
      tenant_id: '',
      user_brand: '',
      user_email: '',
      user_id: '',
      user_name: '',
      company_name: '',
      store_name: '',
      store_city: '',
      user_type: ''
    });
    setIsLoggedIn(false);
      //console.log('⚠️ Estado local limpiado a pesar del error');
    }
  };

  return (
    <div className="App">
      <UserContext.Provider value={{ userData, setUserData, isLoggedIn, handleLogout }}>
        <AuthHandler onLoginSuccess={handleLoginSuccess}>
        <Router>
          <Routes>
            <Route path="/" element={
              !isLoggedIn ? (
                <Login onLoginSuccess={handleLoginSuccess} />
              ) : (
                <Navigate to="/dashboard" />
              )
            } />
            <Route path="/dashboard" element={
              isLoggedIn ? <Menu /> : <Navigate to="/" />
            } />
            <Route path="/precios" element={
              isLoggedIn ? <Precios /> : <Navigate to="/" />
            } />
            <Route path="/usuarios" element={
              isLoggedIn ? <Usuarios /> : <Navigate to="/" />
            } />
            <Route path="/crear-usuario" element={
              isLoggedIn ? <CrearUsuario /> : <Navigate to="/" />
            } />
            <Route path="/transacciones" element={
              isLoggedIn ? <Transacciones /> : <Navigate to="/" />
            } />
            <Route path="/transacciones-pendientes" element={
              isLoggedIn ? <TransaccionesPendientes /> : <Navigate to="/" />
            } />
            <Route path="/campaigns" element={
              isLoggedIn ? <Campaigns /> : <Navigate to="/" />
            } />
            <Route path="/crear-campana" element={
              isLoggedIn ? <CreateCampaign /> : <Navigate to="/" />
            } />
            <Route path="/entidades" element={
              isLoggedIn ? <Entidades /> : <Navigate to="/" />
            } />
            <Route path="/dashboard-ejecutivo" element={
              isLoggedIn ? <DashboardEjecutivo /> : <Navigate to="/" />
            } />
            <Route path="/contratos" element={
              isLoggedIn ? <ContractsViewer /> : <Navigate to="/" />
            } />
            <Route path="/gestion-preguntas" element={
              isLoggedIn ? <GestionPreguntas /> : <Navigate to="/" />
            } />
            <Route path="/gestion-flujos" element={
              isLoggedIn ? <GestionFlujos /> : <Navigate to="/" />
            } />
            <Route path="/codigos-bonos" element={
              isLoggedIn ? <BonusCodes /> : <Navigate to="/" />
            } />
            <Route path="/set-branding" element={
              isLoggedIn ? <SetBranding /> : <Navigate to="/" />
            } />
          </Routes>
        </Router>
        </AuthHandler>
      </UserContext.Provider>
    </div>
  );
}

export default App;
